import { useState } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';

export default function RegistrationForm() {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.2,
  });

  const [formData, setFormData] = useState({
    nombre: '',
    apellido: '',
    email: '',
    celular: '',
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [showZelleModal, setShowZelleModal] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validation
    if (!formData.nombre || !formData.apellido || !formData.email || !formData.celular) {
      alert('Por favor completa todos los campos');
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams({
        nombre: formData.nombre,
        apellido: formData.apellido,
        email: formData.email,
        celular: formData.celular,
        idioma: 'Español',
      }).toString();

      const response = await fetch('https://readdy.ai/api/form/d5ohvs7ecauet1j8bfn0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody,
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          nombre: '',
          apellido: '',
          email: '',
          celular: '',
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section ref={ref} className="relative py-32 px-6 bg-dark-900">
      <div className="max-w-3xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-12"
        >
          <h2 className="font-serif text-5xl md:text-6xl text-gold-400 mb-6">
            Inscripción
          </h2>
          <p className="font-sans text-xl text-warm-200">
            Completa el formulario para comenzar tu peregrinación interior
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="bg-dark-800 border border-gold-500/30 rounded-lg p-8 md:p-12"
        >
          <form id="registration-form" data-readdy-form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="nombre" className="block font-sans text-sm text-warm-200 mb-2">
                  Nombre <span className="text-gold-400">*</span>
                </label>
                <input
                  type="text"
                  id="nombre"
                  name="nombre"
                  value={formData.nombre}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 bg-dark-900 border border-gold-500/30 rounded-lg text-warm-100 font-sans focus:outline-none focus:border-gold-500 transition-colors"
                  placeholder="Tu nombre"
                />
              </div>

              <div>
                <label htmlFor="apellido" className="block font-sans text-sm text-warm-200 mb-2">
                  Apellido <span className="text-gold-400">*</span>
                </label>
                <input
                  type="text"
                  id="apellido"
                  name="apellido"
                  value={formData.apellido}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 bg-dark-900 border border-gold-500/30 rounded-lg text-warm-100 font-sans focus:outline-none focus:border-gold-500 transition-colors"
                  placeholder="Tu apellido"
                />
              </div>
            </div>

            <div>
              <label htmlFor="email" className="block font-sans text-sm text-warm-200 mb-2">
                Correo Electrónico <span className="text-gold-400">*</span>
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 bg-dark-900 border border-gold-500/30 rounded-lg text-warm-100 font-sans focus:outline-none focus:border-gold-500 transition-colors"
                placeholder="tu@email.com"
              />
            </div>

            <div>
              <label htmlFor="celular" className="block font-sans text-sm text-warm-200 mb-2">
                Celular <span className="text-gold-400">*</span>
              </label>
              <input
                type="tel"
                id="celular"
                name="celular"
                value={formData.celular}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 bg-dark-900 border border-gold-500/30 rounded-lg text-warm-100 font-sans focus:outline-none focus:border-gold-500 transition-colors"
                placeholder="+1 234 567 8900"
              />
            </div>

            <div className="bg-dark-900 border border-gold-500/20 rounded-lg p-6">
              <div className="flex items-start gap-3 mb-4">
                <i className="ri-information-line text-2xl text-gold-400 flex-shrink-0"></i>
                <div>
                  <p className="font-sans text-warm-100 font-semibold mb-2">Próximos pasos:</p>
                  <ul className="font-sans text-sm text-warm-200 space-y-1">
                    <li>1. Realiza tu pago por PayPal o Zelle</li>
                    <li>2. Únete a la comunidad de WhatsApp</li>
                    <li>3. Recibirás instrucciones para comenzar</li>
                  </ul>
                </div>
              </div>
            </div>

            {submitStatus === 'success' && (
              <div className="bg-gold-500/20 border border-gold-500 rounded-lg p-4">
                <p className="font-sans text-warm-100 flex items-center gap-2">
                  <i className="ri-checkbox-circle-line text-2xl text-gold-400"></i>
                  ¡Inscripción enviada con éxito! Te contactaremos pronto.
                </p>
              </div>
            )}

            {submitStatus === 'error' && (
              <div className="bg-red-500/20 border border-red-500 rounded-lg p-4">
                <p className="font-sans text-warm-100 flex items-center gap-2">
                  <i className="ri-error-warning-line text-2xl text-red-400"></i>
                  Hubo un error. Por favor intenta nuevamente.
                </p>
              </div>
            )}

            <div className="flex flex-col gap-4">
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full px-8 py-4 bg-gold-500 text-dark-900 font-sans font-semibold text-lg rounded-lg hover:bg-gold-400 transition-all duration-300 whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? 'Enviando...' : 'Enviar Inscripción'}
              </button>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <a
                  href="https://www.paypal.me/johannalvarezmusica?locale.x=en_US"
                  target="_blank"
                  rel="nofollow noopener noreferrer"
                  className="flex items-center justify-center gap-3 px-6 py-4 bg-[#0070BA] text-white font-sans font-semibold text-base rounded-lg hover:bg-[#005EA6] transition-all duration-300 whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-paypal-line text-2xl"></i>
                  PayPal
                </a>

                <a
                  href="https://square.link/u/FphqtTW2?src=sheet"
                  target="_blank"
                  rel="nofollow noopener noreferrer"
                  className="flex items-center justify-center gap-3 px-6 py-4 bg-[#006AFF] text-white font-sans font-semibold text-base rounded-lg hover:bg-[#0055CC] transition-all duration-300 whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-bank-card-2-line text-2xl"></i>
                  Square
                </a>

                <button
                  type="button"
                  onClick={() => setShowZelleModal(true)}
                  className="flex items-center justify-center gap-3 px-6 py-4 bg-dark-900 border-2 border-gold-500 text-gold-400 font-sans font-semibold text-base rounded-lg hover:bg-gold-500/10 transition-all duration-300 whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-bank-card-line text-2xl"></i>
                  Zelle
                </button>
              </div>
            </div>

            <a
              href="https://chat.whatsapp.com/Dp55j3JPXRJ7rwbHSHjHIm"
              target="_blank"
              rel="nofollow noopener noreferrer"
              className="flex items-center justify-center gap-3 w-full px-8 py-4 bg-[#25D366] text-white font-sans font-semibold text-lg rounded-lg hover:bg-[#20BA5A] transition-all duration-300 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-whatsapp-line text-2xl"></i>
              Unirme a WhatsApp
            </a>
          </form>
        </motion.div>
      </div>

      {/* Zelle Modal */}
      {showZelleModal && (
        <div 
          className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 px-6"
          onClick={() => setShowZelleModal(false)}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-dark-800 border-2 border-gold-500/30 rounded-lg p-8 max-w-md w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-6">
              <h3 className="font-serif text-2xl text-gold-400">Pago con Zelle</h3>
              <button
                onClick={() => setShowZelleModal(false)}
                className="w-10 h-10 flex items-center justify-center text-warm-200 hover:text-gold-400 transition-colors cursor-pointer"
              >
                <i className="ri-close-line text-3xl"></i>
              </button>
            </div>

            <div className="bg-white rounded-lg p-6 mb-6">
              <img
                src="https://static.readdy.ai/image/55de03a58447b22c9804f3000d5f2dfc/18989084a2d22874172729e685f56641.png"
                alt="Código QR Zelle"
                className="w-full h-auto"
              />
            </div>

            <div className="text-center space-y-3">
              <p className="font-sans text-warm-100 font-semibold">JOHANN ALVAREZ</p>
              <p className="font-sans text-sm text-warm-200">
                Escanea el código QR con tu app de Zelle para realizar el pago
              </p>
            </div>
          </motion.div>
        </div>
      )}
    </section>
  );
}